import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "./EngagementLetterDetailsPage.module.css";
import SummaryModal from "../SummaryModal/SummaryModal";
import ApprovalSettingsModal from "../ApprovalSettingModal/ApprovalSettingModal";
import CreateOrEditEditorComponent from "../CreateOrEditEditorComponent/CreateOrEditEditorComponent";
import { Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import { ThreeCircles } from "react-loader-spinner";

import UploadSpreadsheetModal from "../UploadSpreadsheetModal/UploadSpreadsheetModal";
import CreateEngagementLetterWizard from "../CreateEngagementLetterWizard/CreateEngagementLetterWizard";
import TemplatePreviewModal from "../template_preview_modal/TemplatePreviewModal";
import { AlertModal } from "../../../Reusables/components/AlertModal/AlertModal";
import CustomButton from "../../../pages/reusable_components/custom_button_component/Button";
import { fetchEngagementTemplateData } from "../CreateEngagementLetterWizard/components/EngagementLetterTemplateSelector/EngagementLetterTemplateSelector.service";
import { createTemplateOptions } from "../CreateEngagementLetterWizard/components/EngagementLetterTemplateSelector/EngagementLetterTemplateSelector.utils";
import CygnusGrid from "../../../pages/reusable_components/CygnusGrid/CygnusGrid";
import {
  downloadEngagementLetters,
  fetchEngagementProcessedTemplatePageData,
  sendELForApproval,
  sendProcessedTemplateForSignature,
  sendProcessedTemplatesForWetSign,
  deleteProcessedTemplates,
  revertELApproval
} from "./EngagementLetterDetailsPage.service";
import {
  CygnusGridRef,
  selectedFirstValuesProvider,
} from "../../../pages/reusable_components/CygnusGrid/CygnusGrid.utils";
import ApprovalStatusRenderer from "./components/ApprovalStatusRenderer/ApprovalStatusRenderer";
import MailMergeStatusRenderer from "./components/MailMergeStatusRenderer/MailMergeStatusRenderer";
import SignatureStatusRenderer from "./components/SignatureStatusRenderer/SignatureStatusRenderer";
import BtnCellRenderer from "./components/BtnCellRenderer/BtnCellRenderer";
import { ColDef, FilterChangedEvent } from "ag-grid-community";
import { ClientGroup } from "./EngagementLetterDetailsPage.types";
import { TimeRenderer } from "../../client_onboarding/ClientsGrid/ClientsGrid.utils";
import { fetchOptions } from "../../client_onboarding/ClientsGrid/ClientsGrid.service";
import { useGlobalStore } from "../../store/globalStore";
import {
  createPartnerProvider,
  createPurposeProvider,
  createUsersProvider,
  createClientTypesProvider,
  createStatusProvider,
  createLabelMap,
  buildServerFilterPayload,
  applyGridModelToUI,
  mergeServerFilters,
  normalizeServerFilters,
  setFilterValues,
} from "../../../pages/reusable_components/CygnusGrid/CygnusGrid.utils";
import {
  saveGridFilters,
  fetchGridFilters,
} from "../../../pages/reusable_components/CygnusGrid/CygnusGrid.service";
import ViewToggle, {
  ViewMode,
} from "../../../Reusables/components/ViewToggle/ViewToggle";
import NextActionRenderer from "./components/NextActionRenderer/NextActionRenderer";
import { Dropdown } from "react-bootstrap";
import WetSignUploadModal from "../WetSignUploadModal/WetSignUploadModal";
import { WetSignELData } from "../WetSignUploadModal/WetSignUploadModal.types";
import { CustomToaster } from "../../reusable_components/Toasters/toaster";
import { initializeELGlobalOptions, initializeClientGlobalOptions,} from "../../utils/globalInitializer";
import { hasFeature } from "../../../utils";
import RecipientsModal from "./components/RecipientsModal/RecipientsModal";
import RecipientsCellRenderer from "./components/RecipientsCellRenderer/RecipientsCellRenderer";

/**
 * EngagementLetterDetailsPage
 * 
 * Main page component for managing engagement letters with rich grid features:
 * - Create individual letters or bulk create via spreadsheet
 * - Send letters for approval workflow
 * - Send letters for e-signature or wet-sign
 * - MyView/AllView toggle with persistent filter preferences
 * - AG Grid with pagination, filtering, and custom cell renderers
 * 
 * Filter Persistence Strategy:
 * - MyView: Filters are saved to backend and automatically restored on page load
 * - AllView: Filters are temporary and never persisted
 * - Toggle between views maintains appropriate filter state for each mode
 */
const EngagementLetterDetailsPage: React.FC<{ transporter: any }> = ({
  transporter,
}) => {
  // ==================== Modal & UI State ====================
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [elId, setELId] = useState<string>("");
  const [elFileName, setELFileName] = useState<string>("");
  const [approvalSettingModal, setApprovalSettingModal] =
    useState<boolean>(false);
  const [createOrEditEditorComponentShow, setCreateOrEditEditorComponentShow] =
    useState(false);
  const [docFileName, setDocFileName] = useState("");
  const [refreshGrid, setRefreshGrid] = useState<boolean>(false);
  const [clientGroups, setClientGroups] = useState<ClientGroup[]>([]);
  const [pdfFileUrl, setPdfFileUrl] = useState<string | undefined>();
  const [templatePreviewModal, setTemplatePreviewModal] = useState<boolean>(false);
  const [uploadSpreadsheetModal, setUploadSpreadsheetModalShow] = useState<boolean>(false);
  const [engagementLetterTemplateOptions, setEngagementLetterTemplateOptions] = useState<any[]>([])
  const [spreadsheetFileUploading, setSpreadsheetFileUploading] = useState<boolean>(false);
  const [addNewWizardShow, setAddNewWizardShow] = useState<boolean>(false);
  const [addNewWizardSaveFlag, setAddNewWizardSaveFlag] = useState<boolean>(false);
  const [resumeDraftWizardShow, setResumeDraftWizardShow] = useState<boolean>(false);
  const [resumeDraftData, setResumeDraftData] = useState<{
    processedTemplateId: number;
    templateId: number;
    clientMasterId: number;
  } | null>(null);
  const [showWetSignUpload, setShowWetSignModal] = useState<boolean>(false);
  const [elData, setELData] = useState<WetSignELData>();
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showActionAlert, setShowActionAlert] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [deleteCount, setDeleteCount] = useState(0);
  const [deleteRiskCount, setDeleteRiskCount] = useState(0);
  const [elDeleteReasonOptions, setElDeleteReasonOptions] = useState<any[]>([]);
  const [selectedDeleteReason, setSelectedDeleteReason] = useState<string>("");
  const [deleteComment, setDeleteComment] = useState<string>("");
  const [actionAlertTitle, setActionAlertTitle] = useState("");
  const [actionAlertMessage, setActionAlertMessage] = useState("");
  const [actionAlertType, setActionAlertType] = useState<"signature" | "wetsign" | null>(null);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);
  const [activeFilterCount, setActiveFilterCount] = useState(0);
  const [filterIndicatorReady, setFilterIndicatorReady] = useState(false);


  // ==================== Action State ====================
  const [sendForSignatureStatus, setSendForSignatureStatus] = useState<boolean>(false);
  const [sendForApprovalDisabledReason, setSendForApprovalDisabledReason] = useState<any>(null);
  const [revertApprovalDisabledReason, setRevertApprovalDisabledReason] = useState<any>(null);
  const [sendForSignatureDisabledReason, setSendForSignatureDisabledReason] = useState<any>(null);
  const [sendForApprovalStatus, setSendForApprovalStatus] = useState<boolean>(false);
  const [revertApprovalStatus, setRevertApprovalStatus] =useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [allApprove, setAllApprove] = useState(false);
  const [clientMasterId, setClientMasterId] = useState();
  const [showRecipientsModal, setShowRecipientsModal] = useState(false);
  const [recipientsModalData, setRecipientsModalData] = useState<any[]>([]);
  const [revertELComment, setRevertELComment] = useState<string>("");
  const [selectedRevertReason, setSelectedRevertReason] = useState("");
  const [elRevertReasonOptions, setElRevertReasonOptions] = useState<any[]>([]);
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);
  const [revertApprovalWarningClientIds, setRevertApprovalWarningClientIds] = useState<string[]>([]);
  const canDeleteEL = hasFeature("EL_DELETE");
  const isFirmAdmin = hasFeature("FRMADMN");
  // ==================== View Mode & Filter State ====================
  /**
   * View Mode Toggle:
   * - "myView": Filters are saved and restored from backend
   * - "allView": Filters are temporary and not persisted
   */
  const [viewMode, setViewMode] = useState<ViewMode>("myView");
  
  /**
   * Saved filter state from backend (only used in MyView)
   * Contains: { gridModel: AGGrid filter model, serverFilter: backend filter params }
   */
  const [saved, setSaved] = useState<any>(null);
  const savedRef = useRef<any>(null); // Ref for accessing saved state in callbacks
  const [savedLoaded, setSavedLoaded] = useState(false); // Flag to prevent premature filter application

  /**
   * viewModeRef: Tracks current viewMode to avoid closure issues in event listeners
   * This ensures the filter change listener always checks the CURRENT viewMode,
   * not the value captured when the listener was created
   */
  const viewModeRef = useRef(viewMode);

  /**
   * suppressFilterEventsRef: Prevents filter change events from triggering saves
   * during programmatic filter updates (e.g., when toggling views)
   */
  const suppressFilterEventsRef = useRef(false);

  /**
   * debouncedTimerRef: Stores timeout ID for debounced filter saves
   * Allows cancellation of pending saves when needed
   */
  const debouncedTimerRef = useRef<number | null>(null);

  /**
   * filterListenerRef: Stores reference to the AG Grid filter change listener
   * Allows removal/re-attachment to prevent false triggers during programmatic changes
   */
  const filterListenerRef = useRef<((event: FilterChangedEvent) => void) | null>(null);

  // ==================== Grid References ====================
  const gridRef = useRef<CygnusGridRef>(null);
  const getActiveGridApi = () => gridRef.current?.api;
  const kpiPayloadHandledRef = useRef(false);
  //const dashboardPayloadPendingApplyRef = useRef(false);

  // ==================== Grid Configuration ====================
  const gridKey = "EngagementLetterDetailsPage.grid";
  const userId = localStorage.getItem("userId");

  const getKpiPayloadFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const encodedPayload = params.get("kpiPayload");

    if (!encodedPayload) return null;

    try {
      return JSON.parse(atob(decodeURIComponent(encodedPayload)));
    } catch (e) {
      return null;
    }
  };

  const clearKpiPayloadFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    params.delete("kpiPayload");

    const nextUrl = `${window.location.pathname}${
      params.toString() ? `?${params.toString()}` : ""
    }`;

    window.history.replaceState({}, "", nextUrl);
  };

  const saveDashboardPayloadFilters = async (payload: any) => {
    if (!payload || viewModeRef.current !== "myView" || !userId) return;

    try {
      await saveGridFilters({
        gridKey: payload.gridKey || gridKey,
        userId: String(userId),
        savedAt: new Date().toISOString(),
        viewMode: "myView",
        gridModel: payload.gridModel || {},
        serverFilter: payload.serverFilter || {},
      });
    } catch (err) {
      console.error("Failed to save dashboard KPI filters:", err);
    }
  };

  /**
   * Filter key mapping: AG Grid column IDs → Backend API field names
   * Used to translate grid filters to server-side query parameters
   */
  const elFilterKeyMap = {
    clientId: "ClientId",
    clientName: "ClientName",
    clientEmail: "ClientEmail",
    clientType: "ClientType",
    officeLocation: "OfficeLocation",
    businessUnit: "BusinessUnit",
    primaryPartner: "PrimaryPartner",
    originatingPartner: "OriginatingPartner",
    clientGroupCode: "ClientGroupCode",
    status: "Status",
    approvalStatus: "ApprovalStatus",
    signatureStatus: "SignatureStatus",
    createdBy: "CreatedBy",
  };

  // ==================== Global Store Options ====================
  const officeLocationOptions = useGlobalStore((s) => s.officeLocationOptions);
  const purposeOptions = useGlobalStore((s) => s.purposeOptions);
  const primaryPartnerOptions = useGlobalStore((s) => s.primaryPartnerOptions);
  const users = useGlobalStore((s) => s.users);
  const clientTypes = useGlobalStore((s) => s.clientTypes);
  const elStatus = useGlobalStore((s) => s.elStatus);
  const elApprovalStatus = useGlobalStore((s) => s.elApprovalStatus);
  const elSignatureStatus = useGlobalStore((s) => s.elSignatureStatus);
  const version = useGlobalStore((s) => s.version);
  const hydrated = useGlobalStore((s) => s.hydrated);
  const [initTriggered, setInitTriggered] = useState(false);

  // ==================== Initialize Global Options ====================
  /**
   * Ensures global dropdown options are loaded on first render
   * Only triggers once when data is missing
   */
  useEffect(() => {
    const fetchELDeleteReasons = async () => {
      const options = await fetchOptions("get_prospect_delete_reason", false);
      setElDeleteReasonOptions(options || []);
    };
    const fetchELRevertReasons = async () => {
      const options = await fetchOptions("get_prospect_revert_reason", false);
      setElRevertReasonOptions(options || []);
    };
    fetchELRevertReasons();
    fetchELDeleteReasons();
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    if (initTriggered) return;

    const isELDataMissing =
      !elStatus?.length ||
      !elApprovalStatus?.length ||
      !elSignatureStatus?.length;

    if (isELDataMissing) {
      setInitTriggered(true);
      initializeELGlobalOptions();
    }
  }, [hydrated, version, elStatus, elApprovalStatus, elSignatureStatus, initTriggered]);

  useEffect(() => {
    if (!hydrated) return;

    const isCommonDataMissing =
      !officeLocationOptions?.length ||
      !purposeOptions?.length ||
      !primaryPartnerOptions?.length ||
      !users?.length ||
      !clientTypes?.length;

    if (!isCommonDataMissing) return;

    initializeClientGlobalOptions();
  }, [
    hydrated,
    officeLocationOptions,
    purposeOptions,
    primaryPartnerOptions,
    users,
    clientTypes,
  ]);

  /**
   * Update column definitions when status options are loaded
   */
  useEffect(() => {
    if (
      !officeLocationOptions?.length ||
      !purposeOptions?.length ||
      !primaryPartnerOptions?.length ||
      !users?.length ||
      !clientTypes?.length ||
      !elStatus?.length ||
      !elApprovalStatus?.length ||
      !elSignatureStatus?.length
    ) {
      return;
    }

    setColumnDefs([...defaultColumnDefs]);
  }, [
    officeLocationOptions,
    purposeOptions,
    primaryPartnerOptions,
    users,
    clientTypes,
    elStatus,
    elApprovalStatus,
    elSignatureStatus,
  ]);

  const filtersReady =
    hydrated &&
    !!officeLocationOptions?.length &&
    !!purposeOptions?.length &&
    !!primaryPartnerOptions?.length &&
    !!users?.length &&
    !!clientTypes?.length &&
    !!elStatus?.length &&
    !!elApprovalStatus?.length &&
    !!elSignatureStatus?.length;

  const allSetFilterValuesByKey = useMemo(() => {
    const values = (items: any[], selector: (item: any) => unknown) =>
      Array.from(new Set((items ?? []).map(selector).map((value) => String(value ?? "").trim()).filter(Boolean)));
    const partners = values(primaryPartnerOptions, (item) => item?.value);

    return {
      ClientType: values(clientTypes, (item) => item?.ReferenceDataValue),
      OfficeLocation: values(officeLocationOptions, (item) => item?.value),
      BusinessUnit: values(purposeOptions, (item) => item?.value ?? item?.code),
      PrimaryPartner: partners,
      OriginatingPartner: partners,
      Status: values(elStatus, (item) => item?.ReferenceDataValue),
      ApprovalStatus: values(elApprovalStatus, (item) => item?.ReferenceDataValue),
      SignatureStatus: values(elSignatureStatus, (item) => item?.ReferenceDataValue),
      CreatedBy: values(users, (item) => item?.UserFullName),
    };
  }, [clientTypes, officeLocationOptions, purposeOptions, primaryPartnerOptions, elStatus, elApprovalStatus, elSignatureStatus, users]);

  const allSetFilterValuesByKeyRef = useRef(allSetFilterValuesByKey);
  allSetFilterValuesByKeyRef.current = allSetFilterValuesByKey;

  // ==================== MyView Filter Lists ====================
  /**
   * Partners to filter by in MyView (based on user's checked preferences)
   * These are automatically applied when in MyView unless saved filters override them
   */
  const partnersForMyView: string[] = useMemo(() => {
    if (!Array.isArray(primaryPartnerOptions)) return [];
    return primaryPartnerOptions
      .filter((p: any) => p?.checked === true)
      .map((p: any) => String(p?.value))
      .filter(Boolean);
  }, [primaryPartnerOptions]);

  /**
   * Office locations to filter by in MyView (based on user's checked preferences)
   */
  const officeLocationsForMyView: string[] = useMemo(() => {
    if (!Array.isArray(officeLocationOptions)) return [];
    return officeLocationOptions
      .filter((loc: any) => loc?.checked === true)
      .map((loc: any) => String(loc?.value))
      .filter(Boolean);
  }, [officeLocationOptions]);

    // ==================== MyView Filter Application Logic ====================
    const syncFilterIndicatorState = useCallback((apiParam?: any) => {
      const api = apiParam || getActiveGridApi();
      const gridModel = api?.getFilterModel?.() || savedRef.current?.gridModel || {};

      const filterCount = Object.keys(gridModel || {}).filter(
        (key) => gridModel[key] !== null && gridModel[key] !== undefined
      ).length;

      const hasGridFilters = filterCount > 0;

      const hasMyViewDefaults =
        viewModeRef.current === "myView" &&
        !savedRef.current?.gridModel &&
        ((partnersForMyView?.length ?? 0) > 0 ||
          (officeLocationsForMyView?.length ?? 0) > 0);

      const hasFilters = hasGridFilters || hasMyViewDefaults;

      const myViewDefaultCount =
        !savedRef.current?.gridModel
          ? Number((partnersForMyView?.length ?? 0) > 0) +
            Number((officeLocationsForMyView?.length ?? 0) > 0)
          : 0;

      setHasActiveFilters(hasFilters);
      setActiveFilterCount(hasFilters ? Math.max(filterCount, myViewDefaultCount) : 0);
    }, [partnersForMyView, officeLocationsForMyView]);

  // ==================== Keep viewModeRef in Sync ====================
  /**
   * Updates viewModeRef whenever viewMode changes
   * This ensures event listeners always see the current viewMode
   */
  useEffect(() => {
    viewModeRef.current = viewMode;
  }, [viewMode]);

  // ==================== Load Saved Filters (MyView Only) ====================
  /**
   * CRITICAL: Only loads saved filters when in MyView
   * 
   * MyView: Fetches and applies saved filter preferences from backend
   * AllView: Clears saved state (filters are temporary)
   * 
   * This prevents AllView filters from being persisted accidentally
   */
  useEffect(() => {
    if (userId == null) return;

    let cancelled = false;

    (async () => {
      setSavedLoaded(false);
      setFilterIndicatorReady(false);

      if (viewModeRef.current === "myView") {
        const navPayload = !kpiPayloadHandledRef.current
          ? getKpiPayloadFromUrl()
          : null;

        if (navPayload) {
          kpiPayloadHandledRef.current = true;
          clearKpiPayloadFromUrl();

          if (cancelled) return;

          const payloadToApply = {
            gridKey: navPayload.gridKey || gridKey,
            savedAt: navPayload.savedAt || new Date().toISOString(),
            viewMode: "myView",
            gridModel: navPayload.gridModel || {},
            serverFilter: navPayload.serverFilter || {},
          };

          setSaved(payloadToApply);
          savedRef.current = payloadToApply;
          //dashboardPayloadPendingApplyRef.current = true;

          await saveDashboardPayloadFilters(payloadToApply);
        } else {
          const res = await fetchGridFilters(userId, gridKey);

          if (cancelled) return;

          setSaved(res || null);
          savedRef.current = res || null;
        }
      } else {
        if (cancelled) return;

        setSaved(null);
        savedRef.current = null;
      }

      if (!cancelled) {
        setSavedLoaded(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId, gridKey, viewMode]);

  // ==================== Column Header Updates ====================
  /**
   * Updates primary partner column header based on view mode
   * Shows tooltip in MyView to indicate switching to AllView for more options
   */
  useEffect(() => {
    setColumnDefs((cols) =>
      cols.map((c) =>
        c.colId === "primaryPartner"
          ? {
            ...c,
            filter: "agSetColumnFilter",
            floatingFilter: true,
            headerComponentParams: {
              viewMode,
              tooltipText:
                "Switch to All View to view all partners / select more if needed.",
            },
          }
          : c
      )
    );
  }, [viewMode]);
  // ==================== Action Handlers ====================
  const send_for_approval = async (rowsOverride?: any[]) => {
    const rows = rowsOverride ?? selectedRows;
    if (!rows || rows.length === 0) return;
    try {
      setSendForApprovalStatus(true);
      await sendELForApproval(rows);
    } finally {
      setSendForApprovalStatus(false);
      forceGridRefresh();
    }
  };

const handleOpenRevertConfirm = () => {
  if (!selectedRows?.length){
       setRevertApprovalWarningClientIds([]);
       return;
  }
  setRevertApprovalWarningClientIds(selectedRows.map((row:any):string => row.ClientId));
  setShowRevertConfirm(true);
  setSelectedRevertReason("")
  setRevertELComment("");
};

const resetRevertConfirm = () => {
  setShowRevertConfirm(false);
  setSelectedRevertReason("");
  setRevertELComment("");
  setRevertApprovalWarningClientIds([]);
  setRevertApprovalStatus(false);
};

const handleConfirmRevert = async () => {
  try {
    await revert_for_approval();
  }finally {
    setRevertELComment("");
    setShowRevertConfirm(false);
  }
};

const revert_for_approval = async (rowsOverride?: any[]) => {
  const rows = rowsOverride ?? selectedRows;
  if (!rows || rows.length === 0) return;

  const filteredData = rows.map(item => ({
  ProcessedTemplateId: item.ProcessedTemplateId,
  }));

  const revertComment= revertELComment.trim()
      ? `${selectedRevertReason}: ${revertELComment.trim()}`
      :  selectedRevertReason;
  try {
    setRevertApprovalStatus(true);
    await revertELApproval(filteredData,revertComment);
  } finally {
    resetRevertConfirm();
    forceGridRefresh();
  }
};

  const send_for_signature = async (rowsOverride?: any[]) => {
    const rows = rowsOverride ?? selectedRows;
    if (!rows || rows.length === 0) return;
    
    const selectedProcessedTemplateIds = rows?.map(
      (row: any) => row.ProcessedTemplateId
    );

    const requestBody: any = {
      ProcessedTemplateIds: selectedProcessedTemplateIds,
    };

    if (!allApprove) {
      requestBody["AutoApprove"] = true;
    }
    
    try {
      setSendForSignatureStatus(true);
      await sendProcessedTemplateForSignature(requestBody);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setSendForSignatureStatus(false);
      handleCancel();
      forceGridRefresh();
    }
  };

  const send_for_wetsign = async () => {
    const rows = selectedRows;
    if (!rows || rows.length === 0) return;

    const ids = rows.map(r => r.ProcessedTemplateId);
    const autoApprove = !allApprove;

    let wetSignSuccess = false;

    try {
      setSendForSignatureStatus(true);

      wetSignSuccess = await sendProcessedTemplatesForWetSign({
        ProcessedTemplateIds: ids,
        SignatureStatus: "Awaiting WetSign",
        AutoApprove: autoApprove,
      });

      CustomToaster({
        type: "info",
        customized_message: "⏳ Preparing the engagement letters for download...",
        api_message: "",
        auto_close: 3500,
      });

    } catch (err) {
      console.error("WetSign Error:", err);
    } finally {
      setSendForSignatureStatus(false);
      handleCancel();
      forceGridRefresh();

      if (wetSignSuccess) {
        downloadEngagementLetters(ids);
      }
    }
  };
  
  const isFullyApproved = (status?: string) => {
    const normalized = (status || "").trim().toLowerCase();
    return normalized === "approved" || normalized === "auto approved";
  };

  const isAutoApprovalEnabled = (value?: string) =>
    (value || "").trim().toUpperCase() === "Y";

  /**
   * Validates selected rows and sets appropriate disabled reason for signature actions
   */
  const updateSendForSignatureDisabledReason = (selectedRows: any[]) => {
    setAllApprove(
      selectedRows?.every(
        (row: any) => isFullyApproved(row.ApprovalStatus)
      )
    );

    let reason = null;
    if (selectedRows?.length === 0) {
      reason = "Please select one or more letters before sending for signature.";
    } else if (selectedRows?.some((row: any) => row.ApprovalStatus.trim().toUpperCase() === "REJECTED")) {
      reason = "Signature is disabled because one or more selected letters have been rejected.";
    } else if (selectedRows.some((row: any) =>
      (row.COIApprovalStatus || "").trim().toUpperCase() === "CONFLICT" || (row.COIApprovalStatus || "").trim().toUpperCase() === "COICONFLICT-REJECTED")
    ) {
    reason ="Signature is disabled because one or more selected letters have a COI conflict.";
    }else if (selectedRows?.some((row: any) => !row.Status.includes("Letter Created"))) {
      reason = "Mail merge status is not completed for one or more selected letters.";
    } else if (
      selectedRows.some(
        (row) => !isAutoApprovalEnabled(row.AutoApproval) && !isFullyApproved(row.ApprovalStatus)
      )
    ) {
      const autoApproveRows = selectedRows.filter((row) => isAutoApprovalEnabled(row.AutoApproval));
      const needsApprovalRows = selectedRows.filter(
        (row) => !isAutoApprovalEnabled(row.AutoApproval) && !isFullyApproved(row.ApprovalStatus)
      );

      if (autoApproveRows.length > 0 && needsApprovalRows.length > 0) {
        reason = "Some selected letters require approval before sending for signature.";
      } else if (needsApprovalRows.length === selectedRows.length) {
        reason = "Selected letters require approval before they can be sent for signature.";
      } else {
        reason = "One or more selected letters are not ready to be sent for signature.";
      }
    } else {
      const invalidSignatureRows = selectedRows?.filter((row: any) => {
        const status = (row.SignatureStatus || "").toLowerCase();
        return !["none", "delivery failed", "failed", "fail"].some(s =>
          status.includes(s)
        );
      });

      if (invalidSignatureRows?.length > 0) {
        if (
          invalidSignatureRows?.some(
            (row: any) => row.SignatureStatus === "Out For Signature"
          )
        ) {
          reason =
            "One or more selected letters have already been sent for signature.";
        } else if (
          invalidSignatureRows?.some(
            (row: any) => row.SignatureStatus === "E-Signed"
          )
        ) {
          reason = "One or more selected letters have already been signed.";
        } else {
          reason =
            "One or more selected letters cannot be sent for signature due to their current signature status.";
        }
      }
    }

    setSendForSignatureDisabledReason(reason);
  };

  /**
   * Validates selected rows and sets appropriate disabled reason for approval actions
   */
  const updateSendForApprovalDisabledReason = (selectedRows: any[]) => {
    let reason = null;
    
    if (selectedRows?.length === 0) {
      reason = "Please select one or more letters before sending for approval.";
    } else if (
      selectedRows?.some((row: any) => !row.Status.includes("Letter Created"))
    ) {
      reason =
        "Mail merge status is not completed for one or more selected letters.";
    } else if (
      selectedRows?.some((row: any) => row.ApprovalStatus?.includes("Approved"))
    ) {
      reason = "One or more selected letters are already approved.";
    } else if (
      selectedRows?.some(
        (row: any) => row.ApprovalStatus === "Pending Approval"
      )
    ) {
      reason =
        "One or more selected letters have already been sent for approval.";
    }

    setSendForApprovalDisabledReason(reason);
  };

const updateRevertApprovalDisabledReason = (selectedRows: any[]) => {
  let reason = null;
 
  if (selectedRows?.length === 0) {
    reason = "Please select one or more letters before reverting approval.";
  } else if (
     selectedRows.some(
    (row: any) =>
      !["approved", "auto approved", "partially approved"].includes(
        (row.ApprovalStatus || "").trim().toLowerCase()
      )
    )
  ) {
    reason =
      "One or more selected letters are not approved and cannot be reverted.";
  }
  setRevertApprovalDisabledReason(reason);
};

  const handleApprovalCheck = (actionCallback: () => void, actionType: "signature" | "wetsign") => {
    if (selectedRows.some(row => isAutoApprovalEnabled(row.AutoApproval))) {
      if (allApprove) {
        actionCallback();
      } else {
        setActionAlertType(actionType);
        setActionAlertTitle("Auto-Approve");
        setActionAlertMessage(
          actionType === "signature"
            ? "If you continue, any unapproved Engagement Letters will be automatically approved and sent for signature."
            : "If you continue, any unapproved Engagement Letters will be automatically approved and sent for WetSign."
        );
        setShowActionAlert(true);
      }
    } else {
      actionCallback();
    }
  };

  const handleSignatureAction = () => {
    handleApprovalCheck(() => {
      send_for_signature();
    }, "signature");
  }

  const handleWetSignAction = () => {
    const allNonAutoApproval = selectedRows.every(row => !isAutoApprovalEnabled(row.AutoApproval));

    if (allNonAutoApproval || allApprove) {
      setActionAlertType("wetsign");
      setActionAlertTitle("WetSign Notice");
      setActionAlertMessage(
        "If you continue, the selected Engagement Letters will be marked as Awaiting WetSign."
      );
      setShowActionAlert(true);
    } else {
      handleApprovalCheck(() => {
        send_for_wetsign();
      }, "wetsign");
    }
  };

  const handleActionAlertContinue = () => {
    if (actionAlertType === "signature") {
      send_for_signature();
    } else if (actionAlertType === "wetsign") {
      send_for_wetsign();
    }
  };

  const handleCancel = () => {
    setShowActionAlert(false);
    setActionAlertType(null);
  };

  // =========== EL Delete Rule Section ===========
  const getDeleteRiskCount = (rows: any[]): number => {
    if (!rows?.length) return 0;

    return rows.filter((row: any) => {
      // Normalize status strings for consistent comparison
      const signatureStatus = (row.SignatureStatus || "")
        .toString()
        .toLowerCase()
        .trim()
        .replace(/\s+/g, " "); // normalize extra spaces

      const approvalStatus = (row.ApprovalStatus || "")
        .toString()
        .toLowerCase()
        .trim()
        .replace(/\s+/g, " ");

      const nextActionLabel = (row.NextAction?.label || "")
        .toString()
        .toLowerCase()
        .trim()
        .replace(/\s+/g, " ");

      const warningSignatureStatuses = new Set([
        "out for signature",
        "partially signed",
      ]);

      const isSignatureWarning = warningSignatureStatuses.has(signatureStatus);
      const isApprovalWarning = approvalStatus.includes("partially approved");
      const isNextActionWarning = nextActionLabel === "check signature status";

      return isSignatureWarning || isApprovalWarning || isNextActionWarning;
    }).length;
  };

  const hasNonDeletableRows = (rows: any[]): boolean => {
    if (!rows?.length) return false;

    const nonDeletableStatuses = new Set([
      "signed",
      "wetsign completed",
      "esigned",
      "e-signed",
    ]);

    return rows.some((row: any) => {
      const signatureStatus = (row.SignatureStatus || "")
        .toString()
        .toLowerCase()
        .trim()
        .replace(/\s+/g, " ");
      return nonDeletableStatuses.has(signatureStatus);
    });
  };

  const deleteDisabled =
  selectedRows.length === 0 || hasNonDeletableRows(selectedRows);

  const deleteTooltipMessage =
    selectedRows.length === 0
      ? "Please select one or more letters to delete."
      : hasNonDeletableRows(selectedRows)
      ? "Signed / E-Signed / WetSign Completed letters cannot be deleted."
      : "";

  const handleConfirmBulkDelete = async () => {
    const ids = (selectedRows || [])
      .map((row: any) => row.ProcessedTemplateId)
      .filter((id: any) => id != null);

    if (!ids.length) {
      setShowDeleteConfirm(false);
      return;
    }

    const combinedDeletionReason = deleteComment.trim()
      ? `${selectedDeleteReason}: ${deleteComment.trim()}`
      : selectedDeleteReason;

    try {
      setDeleteInProgress(true);

      const success = await deleteProcessedTemplates(ids, combinedDeletionReason);

      if (success) {
        setSelectedRows([]);
        setSelectedDeleteReason("");
        setDeleteComment("");
        forceGridRefresh();
      }
    } catch (err) {
      console.error("Bulk delete error:", err);
    } finally {
      setDeleteInProgress(false);
      setShowDeleteConfirm(false);
    }
  };
  const handleOpenDraftWizard = useCallback((data: { processedTemplateId: number; templateId: number; clientMasterId: number }) => {
    setResumeDraftData(data);
    setResumeDraftWizardShow(true);
  }, []);

  // ==================== Grid Styling ====================
  const gridStyle = useMemo(() => ({ height: "85vh", width: "100%" }), []);
  const textParams = { debounceMs: 300, caseSensitive: false };

  // ==================== Filter Value Providers ====================
  /**
   * Creates label maps and value providers for AG Grid filters
   * These handle the display and filtering of dropdown options
   */
  const officeLabelByVal = createLabelMap(
    officeLocationOptions,
    (o) => o.value,
    (o) => o.label
  );
  const partnerLabelByVal = createLabelMap(
    primaryPartnerOptions,
    (o) => o.value,
    (o) => o.label
  );
  const losLabelByVal = createLabelMap(
    purposeOptions,
    (o) => String(o.value ?? o.code),
    (o) => o.label
  );

  const getOfficeLocationValues = createPartnerProvider(officeLocationOptions);
  const getPartnerValues = createPartnerProvider(primaryPartnerOptions);
  const getLosValues = createPurposeProvider(purposeOptions);
  const getUserValues = createUsersProvider(users);
  const getClientTypeValues = createClientTypesProvider(clientTypes);
  const getStatusValues = createStatusProvider(elStatus);
  const getApprovalStatusValues = createStatusProvider(elApprovalStatus);
  const getSignatureStatusValues = createStatusProvider(elSignatureStatus);

  /**
   * Selected-first value providers: Show currently selected filter values at the top
   * Improves UX by making it easy to see what's currently filtered
   */
  const officeLocationValuesSelectedFirst = selectedFirstValuesProvider(
    "officeLocation",
    getOfficeLocationValues
  );
  const partnerValuesSelectedFirst = selectedFirstValuesProvider(
    "primaryPartner",
    getPartnerValues
  );
  const originatingPartnerValuesSelectedFirst = selectedFirstValuesProvider(
    "originatingPartner",
    getPartnerValues
  );
  const clientTypeValuesSelectedFirst = selectedFirstValuesProvider(
    "clientType",
    getClientTypeValues
  );
  const losValuesSelectedFirst = selectedFirstValuesProvider(
    "businessUnit",
    getLosValues
  );
  const statusValuesSelectedFirst = selectedFirstValuesProvider(
    "status",
    getStatusValues
  );
  const approvalStatusValuesSelectedFirst = selectedFirstValuesProvider(
    "approvalStatus",
    getApprovalStatusValues
  );
  const signatureStatusValuesSelectedFirst = selectedFirstValuesProvider(
    "signatureStatus",
    getSignatureStatusValues
  );
  const createdByValuesSelectedFirst = selectedFirstValuesProvider(
    "createdBy",
    getUserValues
  );

  // ==================== Navigation Helper ====================
  const goToPill = (pill: string) => {
    transporter(pill);
  };

  // ==================== Column Definitions ====================
  const defaultColumnDefs: ColDef[] = [
    {
      field: "__select",
      colId: "select",
      headerName: "",
      checkboxSelection: true,
      headerCheckboxSelection: true,
      suppressHeaderMenuButton: true,
      sortable: false,
      width: 50,
      maxWidth: 50,
      filter: false,
      headerClass: "ag-center-select-header",
      pinned: "left",
    },
    {
      headerName: "Client Id",
      field: "ClientId",
      colId: "clientId",
      filter: "agTextColumnFilter",
      filterParams: textParams,
      minWidth: 120,
    },
    {
      headerName: "Client Name",
      field: "ClientName",
      colId: "clientName",
      filter: "agTextColumnFilter",
      filterParams: textParams,
      minWidth: 140,
    },
    {
      headerName: "Client Type",
      field: "ClientType",
      colId: "clientType",
      filter: "agSetColumnFilter",
      filterParams: {
        values: clientTypeValuesSelectedFirst,
        refreshValuesOnOpen: true,
      },
      minWidth: 140,
    },
    {
      field: "OfficeLocation",
      colId: "officeLocation",
      headerName: "Office Location",
      sortable: false,
      minWidth: 130,
      filter: "agSetColumnFilter",
      filterParams: {
        values: officeLocationValuesSelectedFirst,
        refreshValuesOnOpen: true,
        valueFormatter: (p: any) => officeLabelByVal.get(String(p.value)) ?? "",
      },
      valueFormatter: (p) =>
        officeLabelByVal.get(String(p.value)) ?? p.value ?? "",
    },
    {
      field: "BusinessUnit",
      colId: "businessUnit",
      headerName: "Line of Service",
      sortable: false,
      minWidth: 120,
      filter: "agSetColumnFilter",
      filterParams: {
        values: losValuesSelectedFirst,
        refreshValuesOnOpen: true,
        valueFormatter: (p: any) => losLabelByVal.get(String(p.value)) ?? "",
      },
      valueFormatter: (p) =>
        losLabelByVal.get(String(p.value)) ?? p.value ?? "",
    },
    {
      field: "PrimaryPartner",
      colId: "primaryPartner",
      headerName: "Primary Partner",
      sortable: false,
      minWidth: 180,
      filter: "agSetColumnFilter",
      filterParams: {
        values: partnerValuesSelectedFirst,
        refreshValuesOnOpen: true,
        valueFormatter: (p: any) =>
          partnerLabelByVal.get(String(p.value)) ?? "",
      },
      valueFormatter: (p) =>
        partnerLabelByVal.get(String(p.value)) ?? p.value ?? "",
    },
    {
      field: "OriginatingPartner",
      colId: "originatingPartner",
      headerName: "Originating Partner",
      sortable: false,
      filter: "agSetColumnFilter",
      minWidth: 180,
      filterParams: {
        values: originatingPartnerValuesSelectedFirst,
        refreshValuesOnOpen: true,
        valueFormatter: (p: any) =>
          partnerLabelByVal.get(String(p.value)) ?? "",
      },
      valueFormatter: (p) =>
        partnerLabelByVal.get(String(p.value)) ?? p.value ?? "",
    },
    {
      headerName: "Recipients",
      field: "RecipientsJson",
      colId: "clientEmail",
      filter: "agTextColumnFilter",
      minWidth: 200,
      sortable: false,
      cellStyle: {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
      cellRenderer: (params: any) => (
        <RecipientsCellRenderer
          value={params.value}
          setRecipientsModalData={setRecipientsModalData}
          setShowRecipientsModal={setShowRecipientsModal}
        />
      ),
    },
    {
      headerName: "Letter Status",
      field: "Status",
      colId: "status",
      minWidth: 180,
      cellRenderer: MailMergeStatusRenderer,
      filter: "agSetColumnFilter",
      filterParams: {
        values: statusValuesSelectedFirst,
        refreshValuesOnOpen: true,
      },
    },
    {
      headerName: "Approval Status",
      field: "ApprovalStatus",
      colId: "approvalStatus",
      cellRenderer: ApprovalStatusRenderer,
      filter: "agSetColumnFilter",
      minWidth: 190,
      filterParams: {
        values: approvalStatusValuesSelectedFirst,
        refreshValuesOnOpen: true,
      },
    },
    {
      headerName: "Signature Status",
      field: "SignatureStatus",
      colId: "signatureStatus",
      minWidth: 180,
      cellRenderer: SignatureStatusRenderer,
      filter: "agSetColumnFilter",
      filterParams: {
        values: signatureStatusValuesSelectedFirst,
        refreshValuesOnOpen: true,
      },
    },
    {
      headerName: "Created By",
      field: "CreatedBy",
      colId: "createdBy",
      minWidth: 180,
      filter: "agSetColumnFilter",
      filterParams: {
        values: createdByValuesSelectedFirst,
        refreshValuesOnOpen: true,
      },
    },
    {
      headerName: "Created Date",
      field: "CreatedDate",
      colId: "createdDate",
      minWidth: 160,
      cellRenderer: TimeRenderer,
      cellRendererParams: (params: any) => ({
        value: params.value,
      }),
      filter: false,
    },
    {
      headerName: "Updated Date",
      field: "UpdatedDate",
      colId: "updatedDate",
      minWidth: 160,
      cellRenderer: TimeRenderer,
      cellRendererParams: (params: any) => ({
        value: params.value,
      }),
      filter: false,
    },
    {
      field: "nextAction",
      colId: "nextAction",
      headerName: "Next action",
      sortable: false,
      filter: false,
      minWidth: 180,
      pinned: "right",
      cellRenderer: NextActionRenderer,
      cellRendererParams: (params: any) => ({
        data: params.data,
        onSendClientApproval: (row: any) => {
          send_for_approval([row]);
        },
        onSendForSignature: (row: any) => {
          send_for_signature([row]);
        },
        goToApproval: () => {
          goToPill("approval_details");
        },
        onUploadWetSignDocument: () => {
          setShowWetSignModal(true);
          setELData(params.data);
        },
        onResumeDraft: (row: any) => {
          handleOpenDraftWizard({
            processedTemplateId: row.ProcessedTemplateId,
            templateId: row.TemplateId,
            clientMasterId: row.ClientMasterId,
          });
        },
      }),
    },
    {
      headerName: "Action",
      field: "",
      maxWidth: 170,
      sortable: false,
      cellRenderer: BtnCellRenderer,
      cellRendererParams: {
        setPdfFileUrl,
        setTemplatePreviewModal,
        setDocFileName,
        setCreateOrEditEditorComponentShow,
        setApprovalSettingModal,
        setELId,
        setELFileName,
        setIsSummaryModalOpen,
        setClientMasterId,
        setShowWetSignModal,
        setELData,
        setRecipientsModalData,
        openDraftWizard: handleOpenDraftWizard,
      },
      pinned: "right",
      filter: false,
    },
  ];

  const [columnDefs, setColumnDefs] = useState<ColDef[]>(defaultColumnDefs);
  const activeGridColumnsReady = columnDefs.length > 0;

  const savedLoadedRef = useRef(savedLoaded);
  const filtersReadyRef = useRef(filtersReady);
  const activeGridColumnsReadyRef = useRef(activeGridColumnsReady);

  useEffect(() => {
    savedLoadedRef.current = savedLoaded;
  }, [savedLoaded]);

  useEffect(() => {
    filtersReadyRef.current = filtersReady;
  }, [filtersReady]);

  useEffect(() => {
    activeGridColumnsReadyRef.current = activeGridColumnsReady;
  }, [activeGridColumnsReady]);

  const waitForSavedFiltersBeforeFirstFetch = async () => {
    const startedAt = Date.now();
    const maxWaitMs = 5000;

    while (
      Date.now() - startedAt < maxWaitMs &&
      (!savedLoadedRef.current ||
        !filtersReadyRef.current ||
        !activeGridColumnsReadyRef.current)
    ) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  };

  // ==================== First Page Caching ====================
  /**
   * Caches the first page of results for instant view switching
   * Cache is invalidated after 10 minutes or when data is refreshed
   */
  const firstPageCacheRef = useRef<Record<string, any>>({});
  const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

  const partnersKey = (mode: ViewMode) =>
    mode === "myView" ? partnersForMyView.join(",") : "none";

  const myViewKey = (mode: ViewMode) =>
    mode === "myView"
      ? JSON.stringify(savedRef.current?.serverFilter ?? {})
      : "none";

  const stableStringify = (obj: any) =>
    JSON.stringify(obj, Object.keys(obj || {}).sort());

  const makeCacheKey = (
    mode: ViewMode,
    search: string | undefined,
    agFilterModel: any
  ) =>
    `${mode}|${(search ?? "").trim()}|${partnersKey(mode)}|${myViewKey(
      mode
    )}|${stableStringify(agFilterModel)}`;

  /**
   * Wrapped fetch function that:
   * 1. Checks cache for first page results
   * 2. Builds server filter payload from AG Grid filters
   * 3. Merges with MyView partner/office filters if applicable
   * 4. Caches successful first page responses
   */
  const wrappedFetchProcessed = useCallback(
    async (params: any) => {
      await waitForSavedFiltersBeforeFirstFetch();

      const { offset = 0, searchText } = params;

      const incoming = params?.filter ?? params?.filters ?? {};
      let nextFilter: Record<string, any> = { ...incoming };

      const agFilterModel = params?.filterModel ?? {};
      const hasNoAgFilters =
        !agFilterModel || Object.keys(agFilterModel).length === 0;

      const isFirstPage = offset === 0;
      const cacheKey = makeCacheKey(viewMode, searchText, agFilterModel);

      // Check cache for first page with no AG filters
      if (isFirstPage && hasNoAgFilters) {
        const cached = firstPageCacheRef.current[cacheKey];
        if (cached && Date.now() - cached.savedAt < CACHE_TTL_MS) {
          return { records: cached.records, totalCount: cached.totalCount };
        }
      }

      // Build server filter from AG Grid filter model
      const serverFilter = buildServerFilterPayload(
        agFilterModel,
        elFilterKeyMap
      );
      
      // In MyView, merge AG filters with partner/office filters
      if (viewMode === "myView" && Object.keys(serverFilter).length > 0) {
        nextFilter = mergeServerFilters(nextFilter, serverFilter);
      }

      nextFilter = normalizeServerFilters(
        nextFilter,
        allSetFilterValuesByKeyRef.current
      );

      const response: any = await fetchEngagementProcessedTemplatePageData({
        ...params,
        filter: nextFilter,
      });

      syncFilterIndicatorState();
      // Cache first page results
      if (isFirstPage && hasNoAgFilters) {
        firstPageCacheRef.current[cacheKey] = {
          records: response.records,
          totalCount: response.totalCount,
          savedAt: Date.now(),
        };
      }

      return response;
    },
    [viewMode, partnersForMyView, officeLocationsForMyView]
  );

  // ==================== Filter Application ====================
  /**
   * Applies filters when switching between MyView and AllView
   * 
   * CRITICAL: Removes filter listener before changes, then re-attaches after
   * This prevents programmatic filter changes from triggering saves
   * 
   * MyView:
   * - If saved filters exist, apply them
   * - Otherwise, apply default partner/office filters
   * 
   * AllView:
   * - Clear all filters
   * - Remove partner/office filters
   *
   * @param apiParam - Optional API parameter. If provided, uses this instead of getActiveGridApi()
   *                   This is needed during onGridReady when gridRef.current.api might not be set yet
   */
  const applyViewModeFilters = useCallback((apiParam?: any) => {
  if (!savedLoaded || !filtersReady || !activeGridColumnsReady) return;

  const api = apiParam || getActiveGridApi();
  if (!api) return;

  setFilterIndicatorReady(false);
  suppressFilterEventsRef.current = true;

  const hadListener = filterListenerRef.current !== null;

  if (hadListener && filterListenerRef.current) {
    api.removeEventListener("filterChanged", filterListenerRef.current);
  }

  if (viewMode === "myView") {
    if (saved?.gridModel) {
      api.setFilterModel(null);
      applyGridModelToUI(api, saved.gridModel);
    } else {
      api.setFilterModel(null);
      setFilterValues(api, "primaryPartner", partnersForMyView);
      setFilterValues(api, "officeLocation", officeLocationsForMyView);
    }
  } else {
    api.setFilterModel(null);
    setFilterValues(api, "primaryPartner", null);
    setFilterValues(api, "officeLocation", null);
  }

  setTimeout(() => {
    syncFilterIndicatorState(api);
    setFilterIndicatorReady(true);
    suppressFilterEventsRef.current = false;

    if (hadListener && filterListenerRef.current) {
      api.addEventListener("filterChanged", filterListenerRef.current);
    }
  }, 100);
}, [viewMode, saved, savedLoaded, filtersReady, activeGridColumnsReady, partnersForMyView, officeLocationsForMyView, syncFilterIndicatorState,]);

  /**
   * Apply filters when view mode changes or saved filters are loaded
   * This handles: view toggles, partner/office preference changes
   */
  useEffect(() => {
    applyViewModeFilters();
  }, [viewMode, saved, savedLoaded, filtersReady, activeGridColumnsReady, partnersForMyView, officeLocationsForMyView, applyViewModeFilters,]);

  // useEffect(() => {
  //   if (!dashboardPayloadPendingApplyRef.current) return;
  //   if (!savedLoaded || !filtersReady || !activeGridColumnsReady) return;

  //   const api = getActiveGridApi();
  //   if (!api) return;

  //   const payload = savedRef.current;
  //   if (!payload?.gridModel) return;

  //   dashboardPayloadPendingApplyRef.current = false;

  //   // Prevent this programmatic filter apply from being immediately re-saved/cleared
  //   suppressFilterEventsRef.current = true;
  //   setFilterIndicatorReady(false);

  //   api.setFilterModel(null);
  //   applyGridModelToUI(api, payload.gridModel);

  //   window.setTimeout(() => {
  //     firstPageCacheRef.current = {};

  //     syncFilterIndicatorState(api);
  //     setFilterIndicatorReady(true);
  //     suppressFilterEventsRef.current = false;

  //     // Important: force server-side grid refresh using the newly applied filter model
  //     gridRef.current?.refreshGrid?.();
  //   }, 150);
  // }, [
  //   savedLoaded,
  //   filtersReady,
  //   activeGridColumnsReady,
  //   syncFilterIndicatorState,
  // ]);

  // Keeps filter indicator in sync after view changes and filter application
  useEffect(() => {
    const api = getActiveGridApi();
    if (!api) return;

    syncFilterIndicatorState(api);
  }, [viewMode, savedLoaded, syncFilterIndicatorState]);

  /**
   * Keep savedRef in sync with saved state for use in callbacks
   */
  useEffect(() => {
    savedRef.current = saved;
  }, [saved]);

  // ==================== Filter Saving (MyView Only) ====================
  /**
   * Saves current filter state to backend (MyView only)
   * 
   * 1. Gets current filter model from AG Grid
   * 2. Converts to server filter format
   * 3. Saves to backend
   * 4. Updates local state
   */
  const saveMyViewFilters = useCallback(async () => {
    if (viewMode !== "myView") return;
    const api = getActiveGridApi();
    if (!api) return;

    const gridModel = api.getFilterModel();
    const serverFilter = buildServerFilterPayload(gridModel, elFilterKeyMap);

    await saveGridFilters({
      gridKey,
      userId: String(userId ?? ""),
      savedAt: new Date().toISOString(),
      viewMode,
      gridModel,
      serverFilter,
    });

    const payload = { gridModel, serverFilter };
    savedRef.current = payload;
  }, [viewMode, gridKey, userId]);

  /**
   * Debounced save: Waits 400ms after last filter change before saving
   * Prevents excessive API calls when user is actively filtering
   */
  const debouncedSave = useCallback(() => {
    if (debouncedTimerRef.current) {
      window.clearTimeout(debouncedTimerRef.current);
    }
    debouncedTimerRef.current = window.setTimeout(() => {
      if (!suppressFilterEventsRef.current) {
        saveMyViewFilters();
      }
    }, 400);
  }, [saveMyViewFilters]);

  // ==================== Filter Change Listener ====================
  /**
   * Attaches listener to AG Grid's filterChanged event
   * 
   * Only saves filters when:
   * 1. In MyView (checked via viewModeRef to avoid closure issues)
   * 2. Not suppressing events (not during programmatic changes)
   * 
   * Uses debouncedSave to batch rapid filter changes
   */
  const attachMyViewFilterListener = useCallback((api: any) => {
    if (!api) return;

    // Remove old listener if it exists
    if (filterListenerRef.current) {
      api.removeEventListener("filterChanged", filterListenerRef.current);
    }

    // Create new listener that checks current viewMode via ref
    const listener = (_event: FilterChangedEvent) => {
      syncFilterIndicatorState();

      if (viewModeRef.current === "myView" && !suppressFilterEventsRef.current) {
        debouncedSave();
      }
    };

    // Store and attach listener
    filterListenerRef.current = listener;
    api.addEventListener("filterChanged", listener);
  }, [debouncedSave, syncFilterIndicatorState]);

  /**
   * Cleanup: Remove listener on unmount
   */
  useEffect(() => {
    return () => {
      const api = getActiveGridApi();
      if (api && filterListenerRef.current) {
        api.removeEventListener("filterChanged", filterListenerRef.current);
      }
    };
  }, []);

  // ==================== Clear Filters ====================
  /**
   * Clears all filters and resets to defaults
   * 
   * MyView: Clears filters, applies default partner/office filters, saves to backend
   * AllView: Just clears filters (no save needed)
   */
  const clearAllFilters = useCallback(() => {
    const api = getActiveGridApi();
    if (!api) return;

    // Cancel any pending debounced saves
    if (debouncedTimerRef.current) {
      window.clearTimeout(debouncedTimerRef.current);
      debouncedTimerRef.current = null;
    }

    suppressFilterEventsRef.current = true;

    api.setFilterModel(null);

    if (viewMode === "myView") {
      // Apply default MyView filters
      if (partnersForMyView.length > 0) {
        setFilterValues(api, "primaryPartner", partnersForMyView);
      }
      if (officeLocationsForMyView.length > 0) {
        setFilterValues(api, "officeLocation", officeLocationsForMyView);
      }

      // Save to backend
      const gridModel = api.getFilterModel();
      const serverFilter = buildServerFilterPayload(gridModel, elFilterKeyMap);

      savedRef.current = { gridModel, serverFilter };
      setSaved(savedRef.current);

      saveGridFilters({
        gridKey,
        userId: String(userId ?? ""),
        savedAt: new Date().toISOString(),
        viewMode,
        gridModel,
        serverFilter,
      });
    }

    // Refresh filter indicator state after clearing filters
    syncFilterIndicatorState(api);

    // Reset suppress flag after all filter events settle
    Promise.resolve().then(() => {
      suppressFilterEventsRef.current = false;
    });
  }, [viewMode, partnersForMyView, officeLocationsForMyView, userId, gridKey, syncFilterIndicatorState]);

  const handleClearFiltersClick = () => {
    // In MyView, confirm before clearing (overwrites saved preferences)
    if (viewMode === "myView") {
      setShowClearConfirm(true);
    } else {
      clearAllFilters();
    }
  };

  const handleConfirmClear = () => {
    setShowClearConfirm(false);
    clearAllFilters();
  };

  const handleCancelClear = () => {
    setShowClearConfirm(false);
  };

  // ==================== Grid Refresh ====================
  /**
   * Forces a complete grid refresh:
   * 1. Re-applies correct view mode filters
   * 2. Clears first-page cache
   * 3. Triggers CygnusGrid to refetch data (which will use the correct filters)
   */
  const forceGridRefresh = useCallback(() => {
    console.log('forceGridRefresh called');

    // Clear cache immediately
    firstPageCacheRef.current = {};

    const api = getActiveGridApi();

    if (!api) {
      // console.log('Grid not ready - filters will be applied when grid remounts via onGridReady');
      // When grid remounts, onGridReady will apply the correct filters
      gridRef.current?.refreshGrid();
      return;
    }

    // console.log('Grid ready - applying filters before refresh');

    // Grid is ready, apply filters directly before refreshing
    applyViewModeFilters(api);

    // Now refresh with correct filters in place
    gridRef.current?.refreshGrid();
    setTimeout(() => {
      syncFilterIndicatorState(api);
    }, 150);
  }, [applyViewModeFilters, syncFilterIndicatorState]);

  // ==================== Template Data Loading ====================
  useEffect(() => {
    const fetchData = async () => {
      const templateData = await fetchEngagementTemplateData();
      setEngagementLetterTemplateOptions(createTemplateOptions(templateData));
    };
    fetchData();
  }, []);

  /**
   * Update button disabled states when selection changes
   */
  useEffect(() => {
    updateSendForSignatureDisabledReason(selectedRows);
    updateSendForApprovalDisabledReason(selectedRows);
    updateRevertApprovalDisabledReason(selectedRows);
  }, [refreshGrid])

  // ==================== Render ====================
  return (
    <div className={styles.EngagementLetterDetailsPage}>
      {showRecipientsModal && (
        <RecipientsModal
          show={showRecipientsModal}
          onHide={() => setShowRecipientsModal(false)}
          recipients={recipientsModalData}
        />
      )}

      {isSummaryModalOpen && (
        <SummaryModal
          isOpen={isSummaryModalOpen}
          onClose={() => setIsSummaryModalOpen(false)}
          elId={elId}
          elFileName={elFileName}
        />
      )}
      
      {approvalSettingModal && (
        <ApprovalSettingsModal
          showSkipApprovalCheck={false}
          show={approvalSettingModal}
          clientMasterId={clientMasterId!!}
          processedTemplateId={Number(localStorage.getItem("processed_template_id"))}
          onHide={() => setApprovalSettingModal(false)}
          title={"Approval Setting"}
          templateId={null}
        />
      )}
      
      {createOrEditEditorComponentShow && (
        <CreateOrEditEditorComponent
          processedTemplateId={Number(
            localStorage.getItem("processed_template_id")
          )}
          docfilename={docFileName}
          edittype={"EngagementLetter"}
          recipientsJson={recipientsModalData}
          onHide={() => {
            setCreateOrEditEditorComponentShow(false);
            forceGridRefresh();
          }}
        />
      )}
      
      {uploadSpreadsheetModal && (
        <UploadSpreadsheetModal
          show={uploadSpreadsheetModal}
          onHide={() => setUploadSpreadsheetModalShow(false)}
          refresh={() => gridRef.current?.refreshGrid()}
          engagementlettertemplates={engagementLetterTemplateOptions}
          setfileuploading={(flag) => setSpreadsheetFileUploading(flag)}
        />
      )}
      
      {addNewWizardShow && (
        <CreateEngagementLetterWizard
          show={addNewWizardShow}
          onHide={() => {
            setAddNewWizardShow(false);
            forceGridRefresh();
          }}
          engagementlettertemplates={(elt: any) =>
            setEngagementLetterTemplateOptions(elt)
          }
          setapiflag={(flag) => setAddNewWizardSaveFlag(flag)}
          templateOptions={engagementLetterTemplateOptions}
          templatesFetched={true}
        />
      )}

      {resumeDraftWizardShow && resumeDraftData && (
        <CreateEngagementLetterWizard
          show={resumeDraftWizardShow}
          onHide={() => {
            setResumeDraftWizardShow(false);
            setResumeDraftData(null);
            forceGridRefresh();
          }}
          engagementlettertemplates={() => {}}
          setapiflag={() => {}}
          isDraftEdit={true}
          draftProcessedTemplateId={resumeDraftData.processedTemplateId}
          draftTemplateId={resumeDraftData.templateId}
          clientMasterId={resumeDraftData.clientMasterId}
        />
      )}

      {templatePreviewModal && (
        <TemplatePreviewModal
          show={templatePreviewModal}
          pdfFileUrl={pdfFileUrl}
          onHide={() => setTemplatePreviewModal(false)}
        />
      )}

      {showWetSignUpload && (
        <WetSignUploadModal
          show={showWetSignUpload}
          onHide={() => setShowWetSignModal(false)}
          elData={elData!!}
          refresh={() => gridRef.current?.refreshGrid()}
        />
      )}
      
      {/* Alert Modals */}
      <AlertModal
        show={showActionAlert}
        onHide={handleCancel}
        title={actionAlertTitle}
        size="lg"
        message={actionAlertMessage}
        type="warning"
        listItems={[]}
        footerButtons={[
          { label: "Cancel", onClick: handleCancel, type: "quinary" },
          {
            label: "Continue",
            onClick: handleActionAlertContinue,
            progress: sendForSignatureStatus,
            type: "tertiary",
          },
        ]}
      />

      {showClearConfirm && (
        <AlertModal
          show={showClearConfirm}
          onHide={handleCancelClear}
          title="Unsaved Changes"
          message="Reset Filters will remove all your current filters and overwrite your saved My View configuration with the default settings. You will need to reapply any custom filters again."
          type="warning"
          listItems={[]}
          footerButtons={[
            { label: "Cancel", onClick: handleCancelClear },
            { label: "Continue", onClick: handleConfirmClear },
          ]}
        />
      )}
      {showRevertConfirm && (
             <AlertModal
                show={showRevertConfirm}
                onHide={resetRevertConfirm}
                title="Revert EL Approval"
                type="warning"
                message={
                      "You are about to revert approval for " +selectedRows.length +" Engagement Letter(s).\n\t" +
                       "This action will revert the approval status. Do you want to continue?"
                  }
              listItems={
                  revertApprovalWarningClientIds?.length
                  ? revertApprovalWarningClientIds.map(
                  (id: string) => `Client ID: ${id}`
                 ):[]
                }
              bodyContent={
              <div>
                <div style={{ marginBottom: "1rem" }}>
                  <label style={{ display: "block", marginBottom: "0.35rem", fontWeight: 500 }}>
                    Revert EL Reason
                  </label>
                <select
                  value={selectedRevertReason}
                  onChange={(e) =>setSelectedRevertReason(e.target.value)}
                  style={{ width: "100%", padding: "8px" }}
                >
                  <option value="">Select reason</option>
                  {elRevertReasonOptions.map((opt: any) => (
                    <option key={opt.id || opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.35rem",
                    fontWeight: 500,
                  }}>
                  Comment
                </label>
                <textarea
                  value={revertELComment}
                  onChange={(e) => setRevertELComment(e.target.value)}
                  maxLength={100}
                  rows={3}
                  style={{
                      width: "100%",
                      padding: "8px",
                      resize: "vertical",
                  }}
                  placeholder="Add comment if needed"
               />
             </div>
             </div>
             }
              footerButtons={[
                {
                  label: "Cancel",
                  onClick: () => resetRevertConfirm()
                },
                {
                  label: "Revert EL Approval",
                  onClick: handleConfirmRevert,
                  progress: revertApprovalStatus,
                  disabled: !selectedRevertReason,
                  type: "tertiary",
                },
              ]}
            />)}
      
      {showDeleteConfirm && (
        <AlertModal
          show={showDeleteConfirm}
          onHide={() => {
            setShowDeleteConfirm(false);
            setSelectedDeleteReason("");
            setDeleteComment("");
          }}
          title="Delete Engagement Letters"
          type="warning"
          message={
                  deleteRiskCount > 0
                    ? "You are about to delete " +
                      deleteCount +
                      " Engagement Letter(s).\n\n" +
                      "Warning: " +
                      deleteRiskCount +
                      " of the selected letters have already been sent for signatures.\n" +
                      "These Engagement Letters may have already been delivered to the client for signature. Please check and recall them before deleting.\n\n" +
                      "This action will remove them from the grid. Do you want to continue?"
                    : "You are about to delete " +
                      deleteCount +
                      " Engagement Letter(s).\n\n" +
                      "This action will remove them from the grid. Do you want to continue?"
                }
          listItems={[]}
          bodyContent={
            <div>
              <div style={{ marginBottom: "1rem" }}>
                <label style={{ display: "block", marginBottom: "0.35rem", fontWeight: 500 }}>
                  Delete Reason
                </label>
                <select
                  value={selectedDeleteReason}
                  onChange={(e) => setSelectedDeleteReason(e.target.value)}
                  style={{ width: "100%", padding: "8px" }}
                >
                  <option value="">Select reason</option>
                  {elDeleteReasonOptions.map((opt: any) => (
                    <option key={opt.id || opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: "block", marginBottom: "0.35rem", fontWeight: 500 }}>
                  Comment
                </label>
                <textarea
                  value={deleteComment}
                  onChange={(e) => setDeleteComment(e.target.value)}
                  maxLength={100}
                  rows={3}
                  style={{ width: "100%", padding: "8px", resize: "vertical" }}
                  placeholder="Add comment if needed"
                />
              </div>
            </div>
          }
          footerButtons={[
            {
              label: "Cancel",
              onClick: () => {
                setShowDeleteConfirm(false);
                setSelectedDeleteReason("");
                setDeleteComment("");
              },
            },
            {
              label: "Delete",
              onClick: handleConfirmBulkDelete,
              progress: deleteInProgress,
              disabled: !selectedDeleteReason,
              type: "tertiary",
            },
          ]}
        />
      )}

      {/* Main Content */}
      {!createOrEditEditorComponentShow && (
        <>
          {/* Header */}
          <div className={styles.engagementGridTopArea}>
          <Row className={styles.engagementGridHeader}>
            <div className={styles.titleRowLeft}>
              <span className={styles.engagementTitle}>Engagement Letters</span>

              <ViewToggle
                mode={viewMode}
                onChange={(m: ViewMode) => setViewMode(m)}
                size="lg"
              />

              <button
                type="button"
                className={styles.clearButton}
                onClick={handleClearFiltersClick}
              >
                Reset filters
              </button>
            </div>
          </Row>
          
          {/* Action Buttons */}
          <Row className="no-gutter">
            <Col md={12} className="d-flex justify-content-end" style={{ float: "right" }}>

              {canDeleteEL && (
                  <div style={{ marginBottom: "0rem", marginRight: "1rem", marginTop: "-2.1rem" }}>
                    <OverlayTrigger
                      placement="bottom"
                      overlay={
                        deleteTooltipMessage ? (
                          <Tooltip id="bulk-delete-tooltip" style={{ fontSize: "10px" }}>
                            {deleteTooltipMessage}
                          </Tooltip>
                        ) : (
                          <></>
                        )
                      }
                    >
                      <div className="d-inline-block">
                        <CustomButton
                          disabled={deleteDisabled}
                          onclick={() => {
                            const count = selectedRows?.length ?? 0;
                            if (!count) return;
                            // Only gets here when no non-deletable rows are selected
                            setDeleteCount(count);
                            setDeleteRiskCount(getDeleteRiskCount(selectedRows));
                            setShowDeleteConfirm(true);
                          }}
                          content="Delete"
                        />
                      </div>
                    </OverlayTrigger>
                  </div>
                )}

              <div style={{ marginBottom: "0rem", marginRight: "1rem", marginTop: "-2.1rem" }}>
                <CustomButton
                  progress={addNewWizardSaveFlag}
                  onclick={() => setAddNewWizardShow(true)}
                  content={"Create Letter"}
                />
              </div>
              
              <div style={{ marginBottom: "0rem", marginRight: "1rem", marginTop: "-2.1rem" }}>
                <CustomButton
                  progress={spreadsheetFileUploading}
                  onclick={() => setUploadSpreadsheetModalShow(true)}
                  content={"Bulk Create"}
                />
              </div>
        {isFirmAdmin    &&
              <div style={{ marginBottom: "0rem", marginRight: "1rem", marginTop: "-2.1rem" }}>
                <OverlayTrigger
                  placement="bottom"
                  overlay={
                    revertApprovalDisabledReason ? (
                      <Tooltip id="button-tooltip">
                        {revertApprovalDisabledReason}
                      </Tooltip>
                    ) : (
                      <></>
                    )
                  }
                >
                  <div className="d-inline-block">
                    <CustomButton
                      disabled={!!revertApprovalDisabledReason}
                      progress={revertApprovalStatus}
                      onclick={handleOpenRevertConfirm}
                      content={"Revert EL Approval"}
                    />
                  </div>
                </OverlayTrigger>
              </div>
                }
              
              <div style={{ marginBottom: "0rem", marginRight: "1rem", marginTop: "-2.1rem" }}>
                <OverlayTrigger
                  placement="bottom"
                  overlay={
                    sendForApprovalDisabledReason ? (
                      <Tooltip id="button-tooltip">
                        {sendForApprovalDisabledReason}
                      </Tooltip>
                    ) : (
                      <></>
                    )
                  }
                >
                  <div className="d-inline-block">
                    <CustomButton
                      disabled={!!sendForApprovalDisabledReason}
                      progress={sendForApprovalStatus}
                      onclick={() => send_for_approval()}
                      content={"Send EL for Approval"}
                    />
                  </div>
                </OverlayTrigger>
              </div>
              
              <div style={{ marginBottom: "0rem", marginRight: "1rem", marginTop: "-2.1rem" }}>
                <OverlayTrigger
                  placement="bottom"
                  overlay={
                    sendForSignatureDisabledReason ? (
                      <Tooltip id="button-tooltip">{sendForSignatureDisabledReason}</Tooltip>
                    ) : <></>
                  }
                >
                  <div className="d-inline-block">
                    <Dropdown>
                      <Dropdown.Toggle
                        className={styles.signatureDropdown}
                        id="signature-dropdown"
                        disabled={!!sendForSignatureDisabledReason}
                      >
                        Signatures
                      </Dropdown.Toggle>

                      <Dropdown.Menu className={styles.signatureMenu} align="end">
                        <Dropdown.Item
                          className={styles.signatureMenuItem}
                          onClick={handleSignatureAction}
                        >
                          Send for eSign
                        </Dropdown.Item>

                        <Dropdown.Item
                          className={styles.signatureMenuItem}
                          onClick={handleWetSignAction}
                        >
                          Send for WetSign
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </OverlayTrigger>
              </div>
              <div style={{ marginBottom: "0rem", marginTop: "-1.75rem" }}>
                {refreshGrid ? (
                  <div>
                    <ThreeCircles
                      height="20"
                      width="20"
                      color="#4fa94d"
                      wrapperStyle={{}}
                      wrapperClass=""
                      visible={true}
                      ariaLabel="three-circles-rotating"
                      outerCircleColor="var(--theme1_tertiary)"
                      innerCircleColor="var(--theme1_secondary)"
                      middleCircleColor="var(--theme1_primary)"
                    />
                  </div>
                ) : (
                  <Icon.ArrowCounterclockwise
                    onClick={() => forceGridRefresh()}
                    style={{
                      fontSize: 20,
                      float: "right",
                      border: "0px",
                      cursor: "pointer",
                    }}
                  />
                )}
              </div>
            </Col>
          </Row>

          {savedLoaded && filterIndicatorReady && hasActiveFilters && activeFilterCount > 0 && (
            <OverlayTrigger
              placement="bottom"
              overlay={
                <Tooltip id="el-filter-indicator-tooltip" style={{ fontSize: "10px" }}>
                  Filters have been set
                </Tooltip>
              }
            >
              <span className={styles.filterFunnelIconWrap}>
                <span className={styles.elFilterBadge}>
                  <Icon.FunnelFill className={styles.elFilterIcon} />
                  <span className={styles.elFilterCount}>{activeFilterCount}</span>
                </span>
              </span>
            </OverlayTrigger>
          )}
          </div>
          
          {/* Grid */}
          <Row className='no-gutter'>
            <Col md={12}>
              <div style={gridStyle}>
                <CygnusGrid
                  key={`el-grid-${filtersReady && activeGridColumnsReady ? "ready" : "loading"}`}
                  ref={gridRef}
                  columnDefs={columnDefs}
                  gridContext={{
                    gridKey,
                    userId,
                    viewMode,
                    filterKeyMap: elFilterKeyMap,
                  }}
                  fetchData={wrappedFetchProcessed}
                  dataIdentifier={"ProcessedTemplateId"}
                  rowSelection={"multiple"}
                  onLoadingChange={setRefreshGrid}
                  onSelectionChange={(selectedRows: any[]) => {
                    setSelectedRows(selectedRows);
                    updateSendForSignatureDisabledReason(selectedRows);
                    updateSendForApprovalDisabledReason(selectedRows);
                    updateRevertApprovalDisabledReason(selectedRows);
                  }}
                  floatingFilters={true}
                  filterKeyMap={elFilterKeyMap}
                  onGridReady={(p) => {
                    attachMyViewFilterListener(p.api);
                    applyViewModeFilters(p.api);
                  }}
                />
              </div>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default EngagementLetterDetailsPage;
